import { useQuery } from '@tanstack/react-query';

import { useNavigate } from 'react-router-dom';

import { useEffect, useState } from 'react';

import {
  Heading,
  Divider,
  Flex,
  Box,
  Text,
  useColorModeValue,
  Button,
  useDisclosure,
  useToast,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Spinner,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import PolicyUploadModal from 'components/modals/PolicyUploadModal';

import getDateString from 'helpers/getDateString';

import {
  apiCreateSubmission,
  apiGetLeadSubmissions,
  apiCreateLegacyAutoPolicy,
  apiUploadFile,
} from 'api';

import {
  SUBMISSION_TYPE_PERSONAL,
  SUBMISSION_TYPE_COMMERCIAL,
  VALIDATION_TYPE_VALIDATE_AUTO_QUOTE,
  VALIDATION_TYPE_VALIDATE_HOME_QUOTE,
  VALIDATION_TYPE_SPECIALTY_AUTO_LOA,
  VALIDATION_TYPE_MC_LIFE,
  VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY,
  VALIDATION_TYPE_COMMERCIAL_AUTO,
} from 'variables/constants';

import type {
  APILeadOverviewData,
  APISubmissionOverviewData,
} from 'types/api.types';

const TABLE_HEADERS = ['User', 'Type', 'Policies', 'Created At'];

const LeadSubmissions = ({
  lead,
  openModal = false,
}: {
  lead?: APILeadOverviewData;
  openModal?: boolean;
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();

  const [isCreatingSubmission, setIsCreatingSubmission] = useState(false);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const {
    data: submissions,
    isLoading: isLoadingSubmissions,
    error: submissionsError,
  } = useQuery<APISubmissionOverviewData[]>({
    queryKey: ['lead-submissions', lead.id],
    queryFn: () => apiGetLeadSubmissions(lead.id),
  });

  const handleSubmissionClick = (submission: APISubmissionOverviewData) => {
    navigate(`/admin/policies/submission/${submission.id}`);
  };

  const handleModalClose = () => {
    setIsCreatingSubmission(false);
    onModalClose();
  };

  const handleUploadComplete = async (
    uploadedUuid?: string,
    policyType?: ValidationType,
    isLOA?: boolean,
  ) => {
    setIsCreatingSubmission(true);

    try {
      // Determine submission type based on policy type
      const isCommercial =
        policyType === VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY ||
        policyType === VALIDATION_TYPE_COMMERCIAL_AUTO;

      const submissionType = isCommercial
        ? SUBMISSION_TYPE_COMMERCIAL
        : SUBMISSION_TYPE_PERSONAL;

      const submission = await apiCreateSubmission({
        lead_id: lead.id,
        type: submissionType,
      });

      toast({
        title: 'Submission created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // If this is an LOA submission and no UUID was provided, create the LOA policy
      let finalUuid = uploadedUuid;
      if (
        isLOA &&
        policyType === VALIDATION_TYPE_VALIDATE_AUTO_QUOTE &&
        !uploadedUuid
      ) {
        const formData = new FormData();
        formData.append('type', VALIDATION_TYPE_SPECIALTY_AUTO_LOA);
        formData.append('submission_id', submission.id.toString());

        const response = await apiUploadFile(null, formData);
        finalUuid = response.uuid;
      }

      // If this is a life insurance policy and no UUID was provided, create the life policy
      if (policyType === VALIDATION_TYPE_MC_LIFE && !uploadedUuid) {
        const formData = new FormData();
        formData.append('type', VALIDATION_TYPE_MC_LIFE);
        formData.append('submission_id', submission.id.toString());

        const response = await apiUploadFile(null, formData);
        finalUuid = response.uuid;
      }

      // If this is a commercial policy and no UUID was provided, create the commercial policy
      if (isCommercial && !uploadedUuid) {
        const formData = new FormData();
        formData.append('type', policyType!);
        formData.append('submission_id', submission.id.toString());

        const response = await apiUploadFile(null, formData);
        finalUuid = response.uuid;
      }

      // Determine the route based on the uploaded policy type
      const isLife = policyType === VALIDATION_TYPE_MC_LIFE;
      const isSpecialty =
        policyType === VALIDATION_TYPE_VALIDATE_AUTO_QUOTE ||
        policyType === VALIDATION_TYPE_VALIDATE_HOME_QUOTE;
      let route = isLife
        ? `/admin/policies/submission/${submission.id}/${finalUuid}/life`
        : `/admin/policies/submission/${submission.id}/${finalUuid}/validations`;
      if (isSpecialty) {
        route = `/admin/policies/submission/${submission.id}/${finalUuid}/specialty`;
      } else if (isCommercial) {
        route = `/admin/policies/submission/${submission.id}/${finalUuid}/commercial`;
      }

      navigate(route);
      handleModalClose();
    } catch (error) {
      setIsCreatingSubmission(false);
      toast({
        title: 'Failed to create submission',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleLegacyAutoSelect = async () => {
    setIsCreatingSubmission(true);

    try {
      const submission = await apiCreateSubmission({
        lead_id: lead.id,
        type: SUBMISSION_TYPE_PERSONAL,
      });

      const policy = await apiCreateLegacyAutoPolicy(submission.id);

      toast({
        title: 'Submission created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      navigate(
        `/admin/policies/submission/${submission.id}/${policy.uuid}/legacy-auto`,
      );
      handleModalClose();
    } catch (error) {
      setIsCreatingSubmission(false);
    }
  };

  useEffect(() => {
    if (openModal) {
      onModalOpen();
    }
  }, [openModal, onModalOpen]);

  return (
    <Card id="submissions">
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md" letterSpacing="tight" color={textColor}>
          Submissions
        </Heading>
        <Button variant="brand" onClick={onModalOpen}>
          Create submission
        </Button>
      </Flex>

      <Divider />

      {isLoadingSubmissions ? (
        <Flex align="center" justify="center" py="40px">
          <Spinner size="sm" mr="10px" /> Loading submissions...
        </Flex>
      ) : submissionsError ? (
        <Box textAlign="center" py="40px">
          <Text color="red.500">Error loading submissions</Text>
        </Box>
      ) : submissions && submissions.length > 0 ? (
        <Table variant="simple" color="gray.500">
          <Thead>
            <Tr>
              {TABLE_HEADERS.map((header) => (
                <Th
                  key={header}
                  pe="10px"
                  borderColor={borderColor}
                  color="gray.400"
                >
                  {header}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {submissions.map((submission) => (
              <Tr
                key={submission.id}
                cursor="pointer"
                _hover={{
                  bg: 'gray.100',
                }}
                onClick={() => handleSubmissionClick(submission)}
              >
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {submission.user?.first_name} {submission.user?.last_name}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm">
                      {submission.type_display}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm">
                      {submission.policies?.length || 0}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm">
                      {getDateString(new Date(submission.created_at))}
                    </Text>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Box textAlign="center" pt="20px">
          <Text color="gray.500">No submissions found for this lead.</Text>
        </Box>
      )}

      <PolicyUploadModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onUploadComplete={handleUploadComplete}
        onLegacyAutoSelect={handleLegacyAutoSelect}
        submissionId={null}
        isCreatingSubmission={isCreatingSubmission}
        province={lead?.province}
      />
    </Card>
  );
};

export default LeadSubmissions;
